import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useDispatch } from "react-redux";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import SpecsRevisionService from "../../service/SpecsRevisionService";
import {
  loadSpecsEditorImportSpecs,
  resolveImportedVersionName,
} from "./specsEditorUtils";
import { SpecsModel } from "./specsModel/SpecsModel";

function getLatestRevisionId(response: {
  data?: {
    revisions?: Array<{ revisionId?: string | null }>;
    currentDraftRevisionId?: string | null;
    currentPublishedRevisionId?: string | null;
  };
}) {
  return (
    response.data?.revisions?.[0]?.revisionId ??
    response.data?.currentDraftRevisionId ??
    response.data?.currentPublishedRevisionId ??
    null
  );
}

type UseSpecsEditorProductImportArgs = {
  initialImportProduct?: string;
  initialImportVersion?: string;
  setSpecsModel?: Dispatch<SetStateAction<SpecsModel | null>>;
};

export function useSpecsEditorProductImport({
  initialImportProduct = "",
  initialImportVersion = "",
  setSpecsModel,
}: UseSpecsEditorProductImportArgs = {}) {
  const dispatch = useDispatch();
  const [importSpecs, setImportSpecs] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSelectedProduct, setImportSelectedProduct] =
    useState(initialImportProduct);
  const [importSelectedVersion, setImportSelectedVersion] =
    useState(initialImportVersion);
  const [importLoadedRevision, setImportLoadedRevision] = useState<{
    productName: string;
    revisionId: string;
  } | null>(null);
  const initialLoadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const loadKey = `${initialImportProduct}:${initialImportVersion}`;

    if (initialLoadKeyRef.current === loadKey) {
      return;
    }

    initialLoadKeyRef.current = loadKey;
    let cancelled = false;
    setImportLoading(true);
    setImportLoadedRevision(null);
    loadSpecsEditorImportSpecs()
      .then(async (specs: any) => {
        if (cancelled) return;
        let resolvedSpecs = specs;
        let loadedRevision: {
          productName: string;
          revisionId: string;
        } | null = null;

        if (initialImportProduct) {
          try {
            const revisionsResponse =
              await SpecsRevisionService.listSpecRevisions(
                "product",
                initialImportProduct,
                {
                  limit: 1,
                }
              );
            const latestRevisionId = getLatestRevisionId(revisionsResponse);

            if (latestRevisionId) {
              const runtimeResponse =
                await SpecsRevisionService.getSpecRevisionRuntimeSpecs(
                  "product",
                  initialImportProduct,
                  latestRevisionId
                );
              resolvedSpecs = runtimeResponse.data.specs;
              loadedRevision = {
                productName: initialImportProduct,
                revisionId: runtimeResponse.data.revisionId ?? latestRevisionId,
              };
            }
          } catch (error) {
            throw new Error(
              `[SpecsEditor] Failed to load latest saved revision for ${initialImportProduct}`,
              {
                cause: error,
              }
            );
          }
        }

        if (cancelled) return;
        setImportSpecs(resolvedSpecs);
        setImportLoadedRevision(loadedRevision);
        if (initialImportProduct) {
          setImportSelectedProduct(initialImportProduct);
          const resolvedVersion = resolveImportedVersionName(
            resolvedSpecs,
            initialImportProduct,
            initialImportVersion
          );
          if (resolvedVersion) {
            setImportSelectedVersion(resolvedVersion);
          }
        }
      })
      .catch((err: any) => console.error("Failed to load specs:", err))
      .finally(() => {
        if (!cancelled) setImportLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialImportProduct, initialImportVersion]);

  const openImportDialog = useCallback(async () => {
    if (importSpecs) {
      return;
    }

    setImportLoading(true);
    try {
      const specs = await loadSpecsEditorImportSpecs();
      setImportSpecs(specs);
      setImportLoadedRevision(null);
    } catch (err) {
      console.error("Failed to load specs:", err);
    } finally {
      setImportLoading(false);
    }
  }, [importSpecs]);

  const importSelectedProductVersion = useCallback(() => {
    if (!importSpecs || !importSelectedProduct || !importSelectedVersion) {
      return;
    }

    const model = SpecsModel.fromCatalog(
      importSpecs,
      importSelectedProduct,
      importSelectedVersion
    );
    const version = model?.getVersion(importSelectedVersion) ?? null;
    if (!model || !version) {
      return;
    }

    setSpecsModel?.(model);
    dispatch(
      specsEditorActions.hydrateModelLayers({
        layerRuntimeByKey: {},
        layerRuntimeOrder: [],
      })
    );
  }, [
    dispatch,
    importSelectedProduct,
    importSelectedVersion,
    importSpecs,
    setSpecsModel,
  ]);

  const previousImportedProductRef = useRef("");
  useEffect(() => {
    if (!importSpecs) return;

    const key = `${importSelectedProduct}:${importSelectedVersion}`;
    if (
      key !== previousImportedProductRef.current &&
      importSelectedProduct &&
      importSelectedVersion
    ) {
      previousImportedProductRef.current = key;
      importSelectedProductVersion();
    }
  }, [
    importSelectedProduct,
    importSelectedProductVersion,
    importSelectedVersion,
    importSpecs,
  ]);

  return {
    importSpecs,
    importLoadedRevision,
    importLoading,
    importSelectedProduct,
    importSelectedVersion,
    setImportSpecs,
    setImportSelectedProduct,
    setImportSelectedVersion,
    openImportDialog,
    importSelectedProductVersion,
  };
}
